import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Seo } from "../components/Seo";
import { trackEvent } from "../lib/analytics";

const STYLE_OPTIONS = [
  { v: "Tropical Modern", img: "https://images.pexels.com/photos/15391694/pexels-photo-15391694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=500" },
  { v: "Warm Minimalist", img: "https://images.pexels.com/photos/15602699/pexels-photo-15602699.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=500" },
  { v: "Earthy Rustic", img: "https://images.pexels.com/photos/20322620/pexels-photo-20322620.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=500" },
  { v: "Contemporary Concrete", img: "https://images.unsplash.com/photo-1675657144518-025804f1812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB0cm9waWNhbCUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yJTIwaG91c2UlMjBjb25jcmV0ZSUyMHdvb2QlMjBwbGFudHN8ZW58MHx8fHwxNzg0OTYzNTUzfDA&ixlib=rb-4.1.0&q=85" },
];
const SIZE_OPTIONS = ["Under 2,000 sqft", "2,000 – 4,000 sqft", "4,000 – 6,000 sqft", "6,000+ sqft"];
const BUDGET_OPTIONS = ["Under ₹1 Cr", "₹1 – 2.5 Cr", "₹2.5 – 5 Cr", "₹5 Cr+"];
const BED_OPTIONS = ["2 BHK", "3 BHK", "4 BHK", "5+ BHK"];
const TIMELINE_OPTIONS = ["Ready to start", "3 – 6 months", "6 – 12 months", "Just exploring"];

const EASE = [0.16, 1, 0.3, 1];

export default function Quiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    style: "", size: "", budget: "", bedrooms: "", timeline: "",
    name: "", email: "", phone: "", notes: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const steps = [
    { key: "style", title: "Which world feels like home?", label: "Design Style", type: "cards", options: STYLE_OPTIONS },
    { key: "size", title: "How much space do you need?", label: "Built-up Area", type: "pills", options: SIZE_OPTIONS },
    { key: "budget", title: "What's your investment range?", label: "Budget", type: "pills", options: BUDGET_OPTIONS },
    { key: "bedrooms", title: "How many bedrooms?", label: "Configuration", type: "pills", options: BED_OPTIONS },
    { key: "timeline", title: "When would you like to begin?", label: "Timeline", type: "pills", options: TIMELINE_OPTIONS },
    { key: "contact", title: "Where should we send our thoughts?", label: "Your Details", type: "contact" },
  ];

  const current = steps[step];
  const total = steps.length;
  const progress = Math.round(((step + 1) / total) * 100);

  const canNext = () => {
    if (current.type === "contact") return data.name.trim() && /\S+@\S+\.\S+/.test(data.email);
    return !!data[current.key];
  };

  const go = (n) => {
    setDir(n);
    setStep((s) => Math.min(Math.max(s + n, 0), total - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/preferences", {
        name: data.name, email: data.email, phone: data.phone,
        style: data.style, size: data.size, budget: data.budget,
        bedrooms: data.bedrooms, timeline: data.timeline, notes: data.notes,
      });
      setDone(true);
      trackEvent("quiz_submit", {
        style: data.style,
        size: data.size,
        budget: data.budget,
        bedrooms: data.bedrooms,
        timeline: data.timeline,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="quiz-done">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="text-center max-w-xl">
          <div className="w-16 h-16 rounded-full bg-forest text-sand flex items-center justify-center mx-auto mb-8">
            <Check size={28} />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-ink leading-[0.95]">Thank you, {data.name.split(" ")[0]}.</h1>
          <p className="mt-6 text-stone text-lg">We've received your brief for a <span className="text-clay">{data.style}</span> home. A member of Blue Green Studio will reach out within two working days.</p>
          <Link to="/" className="inline-block mt-10 border border-ink/20 px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-ink hover:text-sand transition-colors duration-500">Back to home</Link>
        </motion.div>
      </div>
    );

  return (
    <div className="min-h-screen flex flex-col pt-28 pb-12" data-testid="quiz-page">
      <Seo
        title="Home Design Quiz — Tell Us Your Dream Home"
        description="Two-minute design quiz for prospective clients. Share your style, size, budget and timeline with Blue Green Studio and receive a considered response from our Bangalore architecture studio."
        path="/quiz"
        keywords={["home design quiz", "architect consultation Bangalore", "custom home design brief"]}
      />
      <div className="max-w-4xl w-full mx-auto px-6 md:px-12 flex-1 flex flex-col">
        {/* progress */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-clay">{current.label}</span>
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-stone">{step + 1} / {total}</span>
        </div>
        <div className="h-0.5 bg-ink/10 mb-16 overflow-hidden">
          <motion.div className="h-full bg-forest" animate={{ width: `${progress}%` }} transition={{ ease: EASE, duration: 0.6 }} />
        </div>

        <div className="flex-1">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink mb-12">{current.title}</h1>

              {current.type === "cards" && (
                <div className="grid grid-cols-2 gap-5">
                  {current.options.map((o) => (
                    <button
                      key={o.v}
                      data-testid={`style-option-${o.v.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => { set("style", o.v); }}
                      className={`clip-frame relative aspect-[4/3] text-left border transition-colors duration-300 ${data.style === o.v ? "border-forest ring-2 ring-forest" : "border-ink/10"}`}
                    >
                      <img src={o.img} alt={o.v} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-ink/30" />
                      <span className="absolute bottom-4 left-4 font-serif text-2xl md:text-3xl text-sand">{o.v}</span>
                      {data.style === o.v && <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-forest text-sand flex items-center justify-center"><Check size={16} /></span>}
                    </button>
                  ))}
                </div>
              )}

              {current.type === "pills" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {current.options.map((o) => (
                    <button
                      key={o}
                      data-testid={`option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => set(current.key, o)}
                      className={`text-left px-7 py-6 border text-lg transition-colors duration-300 ${data[current.key] === o ? "bg-forest text-sand border-forest" : "bg-sand border-ink/15 text-ink hover:border-forest"}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}

              {current.type === "contact" && (
                <div className="grid gap-5 max-w-2xl">
                  <input data-testid="quiz-name" value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name *" className="bg-sand border border-ink/15 px-6 py-4 text-lg outline-none focus:border-forest transition-colors" />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input data-testid="quiz-email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className="bg-sand border border-ink/15 px-6 py-4 text-lg outline-none focus:border-forest transition-colors" />
                    <input data-testid="quiz-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className="bg-sand border border-ink/15 px-6 py-4 text-lg outline-none focus:border-forest transition-colors" />
                  </div>
                  <textarea data-testid="quiz-notes" value={data.notes} onChange={(e) => set("notes", e.target.value)} rows={4} placeholder="Anything else about your dream home?" className="bg-sand border border-ink/15 px-6 py-4 text-lg outline-none focus:border-forest transition-colors resize-none" />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* nav */}
        <div className="flex items-center justify-between mt-16">
          <button
            data-testid="quiz-back"
            onClick={() => go(-1)}
            disabled={step === 0}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-stone disabled:opacity-30 hover:text-clay transition-colors"
          >
            <ArrowLeft size={16} /> Back
          </button>

          {step < total - 1 ? (
            <button
              data-testid="quiz-next"
              onClick={() => go(1)}
              disabled={!canNext()}
              className="inline-flex items-center gap-3 bg-forest text-sand px-9 py-4 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-30 hover:bg-clay transition-colors duration-500"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="quiz-submit"
              onClick={submit}
              disabled={!canNext() || submitting}
              className="inline-flex items-center gap-3 bg-forest text-sand px-9 py-4 text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-30 hover:bg-clay transition-colors duration-500"
            >
              {submitting ? "Sending…" : "Submit Brief"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
